import { TFunction } from 'i18next';

import { Notification } from '../types';
import { RANK_UP_NOTIFICATION } from './rank';

// Types written by SoSkills itself: the avatar is the logo, the title "SoSkills".
const SYSTEM_TYPES = [
  'enableNotifications',
  'joinDiscord',
  'joinX',
  'messageToCommunity',
  'messageToUser',
  'newRanking',
  'rateTheApp',
  RANK_UP_NOTIFICATION,
  'topRanking',
  'videoOfTheDay',
  'videoModerated',
];

// Types that open the video when clicked.
const VIDEO_TYPES = ['comment', 'favoriteVideo', 'likeComment', 'likeVideo', 'newVideo', 'videoOfTheDay', 'mention'];

// Types that open an external page (deepLink).
const LINK_TYPES = ['joinDiscord', 'joinX', 'rateTheApp'];

const MAX_WORDS = 15;

// Types of the admin page, in alphabetical order. messageToCommunity, newRanking and topRanking
// are only listed when picked (server side): one copy per user.
const ADMIN_NOTIFICATION_TYPES = [
  'comment',
  'favoriteVideo',
  'follow',
  'likeComment',
  'likeVideo',
  'mention',
  'messageToCommunity',
  'messageToUser',
  'newRanking',
  'newVideo',
  RANK_UP_NOTIFICATION,
  'topRanking',
  'videoModerated',
];

// Admin list: a preview only, the drawer shows everything.
const ADMIN_PREVIEW_LENGTH = 40;

type NotificationTarget =
  // commentId: the comments open on this one.
  | { kind: 'video'; videoId: string; commentId?: string }
  | { kind: 'profile'; userId: string }
  | { kind: 'removal'; reportId: string }
  | { kind: 'link'; url: string }
  | { kind: 'ranking' }
  // rankUp: the ranks are explained.
  | { kind: 'rank'; rank: string };

const isSystemNotification = (n: Notification) => SYSTEM_TYPES.includes(n.type);

// "@username" in a comment: the notification opens the comments on it.
const isMentionNotification = (n: Notification) => n.type === 'mention';

// A long comment is cut, the popup stays readable.
const getShortMessage = (message?: string) => {
  if (!message) {
    return '';
  }
  const words = message.split(' ');
  if (words.length <= MAX_WORDS) {
    return message;
  }
  return `${words.slice(0, MAX_WORDS).join(' ')}…`;
};

// Title in bold (sender or SoSkills) + text, like the notifications screen of the app.
const getNotificationContent = (n: Notification, t: TFunction): { title: string; text: string } => {
  const message = getShortMessage(n.message);
  let title = '';
  if (isSystemNotification(n)) {
    title = 'SoSkills';
  } else if (n.sender && n.sender.username) {
    title = n.sender.username;
  } else {
    title = t('admin.notifs.deletedUser');
  }
  switch (n.type) {
    case 'likeVideo':
      return { title, text: t('admin.notifs.likeVideo') };
    case 'favoriteVideo':
      return { title, text: t('admin.notifs.favoriteVideo') };
    case 'newVideo':
      return { title, text: t('admin.notifs.newVideo') };
    case 'likeComment':
      return { title, text: t('admin.notifs.likeComment', { message }) };
    case 'comment':
      if (n.commentType === 'CHILD') {
        return { title, text: t('admin.notifs.commentChild', { message }) };
      }
      return { title, text: t('admin.notifs.commentParent', { message }) };
    case 'mention':
      // The comment is not shown: the user opens it to see how he was mentioned.
      return { title, text: t('admin.notifs.mention') };
    case 'follow':
      return { title, text: t('admin.notifs.follow') };
    case 'topRanking':
      return { title, text: t('admin.notifs.topRanking', { name: n.gameName }) };
    case 'newRanking':
      return { title, text: t('admin.notifs.newRanking') };
    case RANK_UP_NOTIFICATION:
      return { title, text: t('admin.notifs.rankUp', { name: t(`admin.ranks.${n.rank}`) }) };
    case 'videoOfTheDay':
      return { title: t('admin.notifs.videoOfTheDay'), text: message };
    case 'messageToCommunity':
    case 'messageToUser':
      return { title, text: message };
    case 'videoModerated':
      return { title, text: t('admin.notifs.videoModerated') };
    case 'joinX':
    case 'joinDiscord':
    case 'rateTheApp':
    case 'enableNotifications':
      return { title, text: t(`admin.notifs.${n.type}`) };
    default:
      return { title, text: message || t('admin.notifs.other') };
  }
};

// The comment as the app cuts it: 14 words then " ...".
const getAppShortMessage = (message?: string) => {
  if (!message) {
    return '';
  }
  const words = message.split(' ');
  if (words.length <= 15) {
    return message;
  }
  return `${words.slice(0, 14).join(' ')} ...`;
};

// Admin: the sentence exactly as the app wrote it (its own texts differ a bit from the web ones).
const getReceivedNotificationContent = (n: Notification, t: TFunction): { title: string; text: string } => {
  const { title, text } = getNotificationContent(n, t);
  const app = (key: string, options?: { [key: string]: string }) => t(`admin.notificationsList.app.${key}`, options);
  const message = getAppShortMessage(n.message);
  switch (n.type) {
    case 'likeVideo':
    case 'newVideo':
    case 'favoriteVideo':
    case 'follow':
      return { title, text: `${app(n.type)}.` };
    case 'likeComment':
      return { title, text: `${app('likeComment')}: ${message}` };
    case 'comment':
      return { title, text: `${app(n.commentType === 'CHILD' ? 'commentChild' : 'commentParent')}: ${message}` };
    case 'mention':
    case 'newRanking':
    case 'videoModerated':
      return { title, text: app(n.type) };
    case 'topRanking':
      return { title, text: app('topRanking', { name: n.gameName }) };
    case RANK_UP_NOTIFICATION:
      return { title, text: app('rankUp', { name: t(`admin.ranks.${n.rank}`) }) };
    default:
      return { title, text };
  }
};

// "Ray a aimé ta vidéo.", cut to the length of a short notification.
const getNotificationPreview = (n: Notification, t: TFunction) => {
  const { title, text } = getReceivedNotificationContent(n, t);
  const full = isSystemNotification(n) ? text : `${title} ${text}`;
  if (full.length <= ADMIN_PREVIEW_LENGTH) {
    return full;
  }
  return `${full.slice(0, ADMIN_PREVIEW_LENGTH).trimEnd()}…`;
};

// Where a click on the notification goes, null when there is nothing to open on the web.
const getNotificationTarget = (n: Notification): NotificationTarget | null => {
  if (VIDEO_TYPES.includes(n.type) && n.videoId) {
    if (isMentionNotification(n) && n.commentId) {
      return { kind: 'video', videoId: n.videoId, commentId: n.commentId };
    }
    return { kind: 'video', videoId: n.videoId };
  }
  if (n.type === 'follow' && n.senderId) {
    return { kind: 'profile', userId: n.senderId };
  }
  if (n.type === 'videoModerated' && n.reportId) {
    return { kind: 'removal', reportId: n.reportId };
  }
  if (n.type === RANK_UP_NOTIFICATION && n.rank) {
    return { kind: 'rank', rank: n.rank };
  }
  if (n.type === 'newRanking') {
    return { kind: 'ranking' };
  }
  if (LINK_TYPES.includes(n.type) && n.deepLink && n.deepLink.startsWith('http')) {
    return { kind: 'link', url: n.deepLink };
  }
  return null;
};

// rankUp: the new rank is shown in place of the thumbnail.
const isRankUpNotification = (n: Notification) => n.type === RANK_UP_NOTIFICATION;

// The thumbnail is shown when the notification is about a video.
const hasNotificationThumbnail = (n: Notification) => (
  !!n.thumbnail && n.type !== 'follow' && !n.type.startsWith('message')
);

export type { NotificationTarget };

export {
  ADMIN_NOTIFICATION_TYPES,
  getNotificationContent,
  getNotificationPreview,
  getNotificationTarget,
  getReceivedNotificationContent,
  hasNotificationThumbnail,
  isMentionNotification,
  isRankUpNotification,
  isSystemNotification,
};
